import { readStoredUserId } from './authTokens';
import type { VehiclePricingCategory } from './parkingPricing';
import { callParkingBoxApi } from './parkingBoxApi';
import { normalizeVehicleLicensePlate } from './vehicles';

export type AddCarPayload = {
  userId: string | number;
  plate: string;
  type: VehiclePricingCategory;
};

export type DeleteCarPayload = {
  plate: string;
};

export type NormalizedVehicleApiResponse = {
  status: number | null;
  message: string | null;
};

const normalizeVehicleOwnerId = (value: string | number): string | number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }

  throw new Error('Brak poprawnego user_id dla operacji na pojezdzie.');
};

const normalizeVehicleApiType = (value: VehiclePricingCategory): string => {
  const normalized = value.trim();
  if (!normalized.length) {
    throw new Error('Brak poprawnego typu pojazdu.');
  }
  return normalized;
};

const normalizeVehicleApiPlate = (value: string): string => {
  const normalized = normalizeVehicleLicensePlate(value);
  if (!normalized.length) {
    throw new Error('Brak poprawnego numeru rejestracyjnego.');
  }
  return normalized;
};

const readVehicleApiStatus = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.floor(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) {
      return null;
    }
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return Math.floor(parsed);
    }
  }
  return null;
};

const readVehicleApiMessage = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const normalizeVehicleApiResponse = (payload: unknown): NormalizedVehicleApiResponse => {
  if (!payload || typeof payload !== 'object') {
    return {
      status: null,
      message: null,
    };
  }

  const record = payload as Record<string, unknown>;
  const directStatus = readVehicleApiStatus(record.status);
  const directMessage = readVehicleApiMessage(record.message);
  const nestedSources = [record.data, record._data_];

  for (const nestedSource of nestedSources) {
    if (!nestedSource || typeof nestedSource !== 'object') {
      continue;
    }

    const nestedRecord = nestedSource as Record<string, unknown>;
    const nestedStatus = readVehicleApiStatus(nestedRecord.status);
    const nestedMessage = readVehicleApiMessage(nestedRecord.message);

    if (directStatus !== null || directMessage !== null || nestedStatus !== null || nestedMessage !== null) {
      return {
        status: directStatus ?? nestedStatus,
        message: directMessage ?? nestedMessage,
      };
    }
  }

  return {
    status: directStatus,
    message: directMessage,
  };
};

export const readCurrentVehicleOwnerId = (): string | null => readStoredUserId();

export const addCar = async (payload: AddCarPayload) =>
  callParkingBoxApi(
    {
      action: 'add-car',
      _data_: {
        user_id: normalizeVehicleOwnerId(payload.userId),
        plate: normalizeVehicleApiPlate(payload.plate),
        type: normalizeVehicleApiType(payload.type),
      },
    },
    {
      useStoredToken: true,
    },
  );

export const addCarForCurrentUser = async (payload: Omit<AddCarPayload, 'userId'>) => {
  const userId = readCurrentVehicleOwnerId();
  if (!userId) {
    throw new Error('Brak user_id dla aktualnie zalogowanego uzytkownika.');
  }

  return addCar({
    ...payload,
    userId,
  });
};

export const deleteCar = async (payload: DeleteCarPayload) =>
  callParkingBoxApi(
    {
      action: 'delete-car',
      _data_: {
        plate: normalizeVehicleApiPlate(payload.plate),
      },
    },
    {
      useStoredToken: true,
    },
  );
